/**
 * What stands between a signed-in user and their first order, in order.
 *
 * Two things, each one signature and no gas (`backend/GASLESS-PLAN.md`):
 *
 * | Step | What it is |
 * |---|---|
 * | `SESSION_KEY` | the one-click-trading signature |
 * | `ADD_FUNDS` | USDC from the wallet into the trading balance |
 *
 * **Adding funds is also the permission to settle.** Every deposit lets the
 * settlement contract collect the user's losses from the trading balance, so
 * there is no approval step: until gasless funding there were two more, "wrap"
 * and "approve", each a transaction the user paid gas for — and a fourth,
 * `setApprovalForAll` on the outcome tokens, until `docs/06` §F93.
 *
 * The ticket used to know only the first step. Every other gap surfaced as
 * `INSUFFICIENT BALANCE`, so a wallet holding USDC and nothing else read as
 * broke, with no path from the ticket to the screen that fixed it.
 *
 * **`null` is unread and never counts as missing.** A step this module cannot
 * see is not offered, so a read that failed falls back to the ticket's old
 * behaviour rather than sending a user to sign something they may already have
 * signed.
 *
 * In `domain/` because it imports no React and no viem (invariant 10).
 */

export type SetupStep = 'SESSION_KEY' | 'ADD_FUNDS'

/** The order the steps are offered in, and the order the modal lists them. */
export const SETUP_ORDER: readonly SetupStep[] = ['SESSION_KEY', 'ADD_FUNDS']

export interface SetupFacts {
  /** A session key is armed. */
  keyArmed: boolean
  /** USDC in the wallet, in micro-units — what can be added. */
  usdc: bigint | null
  /** The trading balance, in micro-units. */
  tradingBalance: bigint | null
  /**
   * What the order being composed needs, in micro-units. `0` for no order — the
   * steps are then judged against "able to trade at all".
   */
  neededMicroUsdc: bigint
}

/** What "able to trade at all" means with no order in hand: one dollar. */
export const TRADABLE_MICRO_USDC = 1_000_000n

/**
 * The steps still to do, in `SETUP_ORDER`.
 *
 * **ADD_FUNDS** when the wallet holds USDC and the trading balance is short of
 * what is needed (one dollar, with no order in hand). Without USDC there is
 * nothing to add, and the ticket's `INSUFFICIENT BALANCE` is the honest answer.
 */
export function missingSetup(facts: SetupFacts): SetupStep[] {
  const need = facts.neededMicroUsdc > 0n ? facts.neededMicroUsdc : TRADABLE_MICRO_USDC
  const steps: SetupStep[] = []
  if (!facts.keyArmed) steps.push('SESSION_KEY')
  if (facts.usdc !== null && facts.usdc > 0n && facts.tradingBalance !== null && facts.tradingBalance < need) {
    steps.push('ADD_FUNDS')
  }
  return steps
}
